import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Alert, Box, Button, Divider, Skeleton, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Image } from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { ImageUploaderWrapper } from "./styles";
import Cropper, { Area } from "react-easy-crop";
import ModalComponent from "../../Modals";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { useGetManageWeddingInvitationAnimeQuery } from "../../../store/services/invitations/invitations.service";
import {
	IAnime,
	INVITATION_PRICING_PLANS_ENUM,
	IPayedInvitation,
	PRICING_PLAN_TYPE
} from "../../../store/services/invitations/dto/invitations.dto";
import { convertHEICToJPEG } from "../../../utils/convertHeicToJPEG";
import { UpgradeBtn, WhiteCrown } from "../../../Pages/Invitations/styles";

const getCroppedImg = (imageSrc: string, cropArea: Area): Promise<string> => {
	return new Promise(async (resolve, reject) => {
		try {
			let imgSrc = imageSrc;
			const image = new window.Image();
			image.crossOrigin = "anonymous"; // Allow CORS
			image.src = imgSrc;

			image.onload = () => {
				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d");

				if (!ctx) {
					reject(new Error("Failed to get canvas context"));
					return;
				}

				// Set canvas size to cropped area
				canvas.width = cropArea.width;
				canvas.height = cropArea.height;

				// Draw cropped image onto canvas
				ctx.drawImage(
					image,
					cropArea.x,
					cropArea.y,
					cropArea.width,
					cropArea.height,
					0,
					0,
					cropArea.width,
					cropArea.height
				);

				// Convert to base64 URL
				resolve(canvas.toDataURL("image/jpeg"));
			};

			image.onerror = (error: any) => reject(error);
		} catch (error) {
			reject(error);
		}
	});
};

const FileUploader = ({
	setFieldValueState,
	fieldValueState: imageSrc,
	isDisabled,
	setIsDisabled,
	max_image_size = 2,
	hidden,
	fieldName,
	visibilityChange
}: {
	fieldValueState: string | IAnime;
	setFieldValueState: React.Dispatch<React.SetStateAction<string | IAnime>>;
	isDisabled: boolean;
	setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
	max_image_size: number;
	fieldName: string;
	hidden?: boolean;
	visibilityChange?: (status: boolean, name: string) => void;
}) => {
	const MAX_MB = max_image_size;
	const MAX_FILE_SIZE = MAX_MB * 1024 * 1024;
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const { data: AvatarList, isLoading } = useGetManageWeddingInvitationAnimeQuery();

	const mappedImageSrc = useMemo(() => (typeof imageSrc === "string" ? imageSrc : imageSrc.main_image), [imageSrc]);
	const disableImageCrop = useMemo(() => mappedImageSrc.includes("partners0"), [mappedImageSrc]);
	const onDrop = useCallback(async (acceptedFiles: File[]) => {
		if (acceptedFiles.length > 0) {
			setIsDisabled(true);
			let file = acceptedFiles[0];
			file = await convertHEICToJPEG(file);
			const img: HTMLImageElement = document.createElement("img");
			img.src = URL.createObjectURL(file);
			img.onload = () => {
				setIsDisabled(false);
				if (img.width < 200 || img.height < 200) {
					notifyError({
						data: { message: "Image must be at least 200x200 pixels." }
					} as IError);
					return;
				}

				// If dimensions are valid → read file
				const reader = new FileReader();
				reader.onload = () => {
					setFieldValueState(reader.result as string);
				};
				reader.readAsDataURL(file);
			};

			img.onerror = () => {
				setIsDisabled(false);
				notifyError({
					data: { message: "Can not load image" }
				} as IError);
			};
		}
	}, []);

	const { getRootProps, getInputProps, fileRejections } = useDropzone({
		onDrop,
		accept: { "image/*": [] },
		multiple: false,
		validator: function (file) {
			if (file.size > MAX_FILE_SIZE) {
				return {
					code: "Size exceeded",
					message: `Image limit exceeded (Max: ${MAX_MB}MB)`
				};
			}
			return null;
		}
	});

	useEffect(() => {
		if (fileRejections.length > 0) {
			fileRejections.forEach(item => {
				notifyError({
					data: { message: `${item.file.name} can not be uploaded, ${item.errors.map(err => err.message).join(", ")}` }
				} as IError);
			});
		}
	}, [fileRejections]);

	const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
		setCroppedAreaPixels(croppedAreaPixels);
	}, []);

	const cropImage = async () => {
		if (!mappedImageSrc || !croppedAreaPixels) return;

		try {
			const croppedImage = await getCroppedImg(mappedImageSrc, croppedAreaPixels);
			setFieldValueState(croppedImage); // Update displayed image
			setIsModalOpen(false);
		} catch (error) {
			notifyError(error as IError);
		}
	};

	const LoadingSkeleton = () => {
		const loadingArray = new Array(6).fill(0);
		return (
			<Box display="flex" style={{ flexFlow: "row wrap" }} gap={2}>
				{loadingArray.map((_, index) => (
					<Box key={index} width={"80px"}>
						<Skeleton variant={"rectangular"} height={"75px"} />
					</Box>
				))}
			</Box>
		);
	};
	return (
		<ImageUploaderWrapper p={2}>
			<Box display={"flex"} justifyContent={"flex-end"} width={"100%"} my={1} alignItems={"center"}>
				<Typography>{hidden ? "Hidden" : "Visible"}</Typography>
				<Switch checked={!hidden} onChange={() => visibilityChange && visibilityChange(!hidden, fieldName)} />
			</Box>
			{mappedImageSrc && (
				<Box display={"flex"} flexDirection={"column"} alignItems={"center"} justifyContent={"center"}>
					{disableImageCrop ? <Box height={20} /> : <p>Click image to crop</p>}
					{isDisabled ? (
						<Skeleton variant={"rectangular"} height={"300px"} width={"80%"} />
					) : (
						<img
							src={mappedImageSrc}
							alt="Uploaded"
							className={"image_view"}
							onClick={() => !disableImageCrop && setIsModalOpen(true)}
						/>
					)}

					<Box height={10} />
				</Box>
			)}
			<div {...getRootProps()} className="border-dashed border-2 p-8 text-center cursor-pointer">
				<input {...getInputProps()} />
				<Image className={"icon"} />
				<Typography align={"center"}>Drag & drop image here, or click to select</Typography>
				<Typography align={"center"}>(Max: {MAX_MB}MB)</Typography>
			</div>
			<Box height={20} />
			<Divider />
			<Box height={20} />
			<Typography textAlign={"center"}>Or select an Avatar from the below list</Typography>
			<Box height={10} />
			<Alert severity="info">Choosing avatar reflects on all invitation pictures</Alert>
			<Box height={30} />
			{isLoading ? (
				<LoadingSkeleton />
			) : (
				<Box className={"image_list_wrapper"}>
					{AvatarList ? (
						((AvatarList as IAnime[]) || ([] as IAnime[])).map(item => (
							<Box
								key={item.id}
								onClick={() => setFieldValueState(item)}
								style={{ cursor: "pointer" }}
								className={`image ${item.id === (imageSrc as IAnime).id ? "selected" : ""}`}
							>
								<img srcSet={`${item.main_image}`} src={`${item.main_image}`} alt={"Animation"} loading="lazy" />
							</Box>
						))
					) : (
						<> </>
					)}
				</Box>
			)}{" "}
			{isModalOpen && (
				<ModalComponent
					open={isModalOpen}
					handleCloseModal={() => setIsModalOpen(false)}
					width={isSmallerScreen ? "95%" : "700px"}
				>
					<>
						<h2>Crop Image</h2>
						<div style={{ position: "relative", width: "100%", height: "500px" }}>
							<Cropper
								image={mappedImageSrc as string}
								crop={crop}
								zoom={zoom}
								aspect={1}
								onCropChange={setCrop}
								onZoomChange={setZoom}
								onCropComplete={onCropComplete}
							/>
						</div>
						<Box mt={3} width={"100%"} display={"flex"} justifyContent={"center"}>
							<Button variant={"contained"} color={"primary"} onClick={cropImage} data-sentry-label={"Confirm Crop"}>
								Confirm Crop
							</Button>
						</Box>
					</>
				</ModalComponent>
			)}
		</ImageUploaderWrapper>
	);
};

export default FileUploader;
